import React, { Suspense, useMemo, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows, QuadraticBezierLine } from '@react-three/drei';
import * as THREE from 'three';
import NetworkBlade from './NetworkBlade';

const Cable = ({ start, end, color }) => {
    // Generate a natural drooping midpoint for the cable spline
    const mid = useMemo(() => {
        // We use Math.abs to give thicker/longer cables more droop
        const distance = start.distanceTo(end);
        // Tighter professional look: less droop, smaller max droop
        const droop = Math.min(distance * 0.15, 0.4);
        return new THREE.Vector3(
            (start.x + end.x) / 2,
            Math.min(start.y, end.y) - droop, // Droop down
            Math.max(start.z, end.z) + 0.05 + (distance * 0.05) // Bow outwards slightly less
        );
    }, [start, end]);

    return (
        <QuadraticBezierLine
            start={start}
            end={end}
            mid={mid}
            color={color || "#10b981"}
            lineWidth={3}
            dashed={false}
        />
    );
};

const NetworkStack = ({ items, onBladeClick, authenticatedFetch = fetch }) => {
    const [connections, setConnections] = useState([]);
    const [showCables, setShowCables] = useState(true);

    const rackId = items.length > 0 ? items[0].rack_id : null;

    useEffect(() => {
        if (rackId) {
            authenticatedFetch(`/api/network/connections?rackId=${rackId}`)
                .then(res => res.json())
                .then(data => setConnections(data))
                .catch(err => console.error("Failed to load connections:", err));
        }
    }, [authenticatedFetch, rackId]);

    // Separate ISP/Modem devices from rack-mounted devices
    const rackDevices = useMemo(() => items.filter(i => i.type !== 'ISP' && i.type !== 'Modem'), [items]);
    const externalDevices = useMemo(() => items.filter(i => i.type === 'ISP' || i.type === 'Modem'), [items]);

    // Complex helper to compute exact world 3D coordinates for any given device's port
    const getGlobalPortPosition = (deviceId, portIndex) => {
        const device = items.find(d => d.id === deviceId);
        if (!device) return null;

        const specs = typeof device.specs === 'string' ? JSON.parse(device.specs) : (device.specs || {});
        const portBanks = specs.portBanks || [];
        
        let targetBank = null;
        let bIdx = 0;
        let localPortIndex = 0;
        let currentCount = 0;

        for (let i = 0; i < portBanks.length; i++) {
            const count = portBanks[i].count || 0;
            if (portIndex < currentCount + count) {
                targetBank = portBanks[i];
                bIdx = i;
                localPortIndex = portIndex - currentCount;
                break;
            }
            currentCount += count;
        }

        // If port not explicitly in a bank, fallback
        if (!targetBank) {
            if (device.type === 'ISP' || device.type === 'Modem') {
                targetBank = { count: 4 }; // Hardcoded fallback for ISP
                localPortIndex = Math.min(portIndex, 3);
            } else {
                return null;
            }
        }

        const isISP = device.type === 'ISP' || device.type === 'Modem';

        if (isISP) {
            const totalPortsToRender = portBanks.length > 0 
                ? portBanks.reduce((sum, b) => sum + (b.count || 0), 0)
                : 4; // fallback
            
            const portsWidth = (totalPortsToRender - 1) * 0.06;
            const startX = -(portsWidth / 2);
            
            const localX = startX + 0.25 + (portIndex * 0.06);
            const localY = -0.01;
            const localZ = 0.252;

            const idx = externalDevices.findIndex(d => d.id === deviceId);
            const worldX = idx * 1.0 - (externalDevices.length - 1) * 0.5 + localX;
            const worldY = 3.45 + localY;
            const worldZ = 0.8 + localZ; 
            return new THREE.Vector3(worldX, worldY, worldZ);
        }

        const U_HEIGHT = 0.15;
        const h = (device.height_u || 1) * U_HEIGHT;
        const deviceYPos = ((device.position_u || 1) - 1) * U_HEIGHT + (h / 2);

        if (device.type === 'Patch Panel') {
            const USABLE_WIDTH = 1.72;
            const totalPorts = portBanks.reduce((sum, b) => sum + (b.count || 0), 0);
            if (totalPorts > 0) {
                const portSpacing = USABLE_WIDTH / totalPorts;
                const startX = -(USABLE_WIDTH / 2) + portSpacing / 2;
                const localX = startX + portIndex * portSpacing;
                // Patch panels don't have Y offsets per row
                return new THREE.Vector3(localX, -3.1 + deviceYPos, 0.901);
            }
        }

        // Standard Rack Device Port Bank Math (from NetworkBlade.jsx)
        const banksByOffset = {};
        portBanks.forEach((b, origIdx) => {
            const key = String(b.xOffset || 0);
            if (!banksByOffset[key]) banksByOffset[key] = [];
            banksByOffset[key].push({ bank: b, origIdx });
        });

        const bankPositions = {};
        Object.values(banksByOffset).forEach(group => {
            const GAP = 0.07;
            const widths = group.map(({ bank }) => {
                const colsUsed = Math.min(bank.count || 0, bank.columns || 1);
                return colsUsed * 0.06;
            });
            const totalWidth = widths.reduce((s, w) => s + w, 0) + GAP * (group.length - 1);
            let cursor = ((group[0].bank.xOffset || 0) * 1.3) - (totalWidth / 2) + 0.03;
            
            group.forEach(({ origIdx }, i) => {
                bankPositions[origIdx] = cursor;
                cursor += widths[i] + GAP;
            });
        });

        const baseX = bankPositions[bIdx];
        const maxCols = targetBank.columns || 1;
        const col = localPortIndex % maxCols;
        const row = Math.floor(localPortIndex / maxCols);

        const localX = baseX + (col * 0.06);
        const localY = -(row * 0.05) + 0.02; // Y_RAISE
        const localZ = 0.901;

        const worldX = localX;
        const worldY = -3.1 + deviceYPos + localY;
        const worldZ = localZ;

        return new THREE.Vector3(worldX, worldY, worldZ);
    };

    return (
        <div className="w-full h-full relative" style={{ background: 'linear-gradient(180deg, #080808 0%, #0a0a0a 40%, #0d0d0d 100%)' }}>
            {/* UI Overlay */}
            <div className="absolute top-6 left-6 z-10 flex gap-3">
                <button 
                    onClick={() => setShowCables(!showCables)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border transition-all shadow-lg backdrop-blur-md ${showCables ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-white/5 text-white/40 border-white/10 hover:bg-white/10 hover:text-white/60'}`}
                >
                    {showCables ? 'Hide Cables' : 'Show Cables'}
                </button>
            </div>

            <Canvas
                camera={{ position: [8, 6, 8], fov: 45 }}
                gl={{
                    powerPreference: "default",
                    failIfMajorPerformanceCaveat: false,
                    preserveDrawingBuffer: true,
                    antialias: true
                }}
                frameloop="always"
                dpr={[1, 2]}
            >
                <Suspense fallback={null}>
                    <OrbitControls
                        enablePan={true}
                        minPolarAngle={0}
                        maxPolarAngle={Math.PI / 1.5}
                        autoRotate={false}
                    />

                    {/* Lighting — Cinematic & Premium */}
                    <Environment preset="night" />
                    <ambientLight intensity={0.3} color="#b4c6ef" />
                    <spotLight position={[10, 12, 10]} angle={0.2} penumbra={1} intensity={1.5} castShadow color="#e8f0ff" />
                    <spotLight position={[-5, 8, -5]} angle={0.3} penumbra={1} intensity={0.4} color="#a78bfa" />
                    <pointLight position={[0, -2, 5]} intensity={0.2} color="#60a5fa" />

                    {/* Cables Layer */}
                    {showCables && connections.map(conn => {
                        const start = getGlobalPortPosition(conn.source_device_id, conn.source_port);
                        const end = getGlobalPortPosition(conn.target_device_id, conn.target_port);
                        
                        if (start && end) {
                            return (
                                <Cable 
                                    key={conn.id} 
                                    start={start} 
                                    end={end} 
                                    color={conn.cable_color} 
                                />
                            );
                        }
                        return null;
                    })}

                    {/* The Rack Cabinet Frame */}
                    <group position={[0, 0, 0]}>
                        {/* Base */}
                        <mesh position={[0, -3.2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                            <boxGeometry args={[2.5, 2.5, 0.1]} />
                            <meshStandardMaterial color="#111111" metalness={0.3} roughness={0.8} />
                        </mesh>

                        {/* Vertical Rails */}
                        <mesh position={[-1.1, 0, 1]} castShadow receiveShadow>
                            <boxGeometry args={[0.1, 6.5, 0.1]} />
                            <meshStandardMaterial color="#1a1a1a" metalness={0.9} roughness={0.15} />
                        </mesh>
                        <mesh position={[1.1, 0, 1]} castShadow receiveShadow>
                            <boxGeometry args={[0.1, 6.5, 0.1]} />
                            <meshStandardMaterial color="#1a1a1a" metalness={0.9} roughness={0.15} />
                        </mesh>

                        {/* Small shelf bracket for external devices */}
                        {externalDevices.length > 0 && (
                            <mesh position={[0, 3.35, 0.5]} rotation={[-Math.PI / 2, 0, 0]}>
                                <boxGeometry args={[1.5, 1.0, 0.04]} />
                                <meshStandardMaterial color="#151515" metalness={0.3} roughness={0.8} />
                            </mesh>
                        )}
                    </group>

                    {/* Rack-Mounted Devices */}
                    <group position={[0, -3.1, 0]}>
                        {rackDevices.map((item) => {
                            const U_HEIGHT = 0.15;
                            const h = (item.height_u || 1) * U_HEIGHT;
                            const yPos = ((item.position_u || 1) - 1) * U_HEIGHT + (h / 2);

                            return (
                                <NetworkBlade
                                    key={item.id}
                                    position={[0, yPos, 0]}
                                    type={item.type}
                                    status={item.status}
                                    details={{ ...item, name: item.model_name || item.type }}
                                    onClick={onBladeClick}
                                />
                            );
                        })}
                    </group>

                    {/* External Devices (ISP/Modem) — On top shelf */}
                    <group position={[0, 3.45, 0.8]}>
                        {externalDevices.map((item, idx) => (
                            <NetworkBlade
                                key={item.id}
                                position={[idx * 1.0 - (externalDevices.length - 1) * 0.5, 0, 0]}
                                type={item.type}
                                status={item.status}
                                details={{ ...item, name: item.model_name || item.type }}
                                onClick={onBladeClick}
                            />
                        ))}
                    </group>

                    <ContactShadows opacity={0.3} scale={10} blur={2.5} far={4} color="#000000" />

                    {/* Subtle ground grid for depth */}
                    <gridHelper args={[20, 40, '#111111', '#0a0a0a']} position={[0, -3.25, 0]} />
                </Suspense>
            </Canvas>

            {/* Overlay Text */}
            <div className="absolute bottom-4 left-4 text-white/10 text-xs font-mono pointer-events-none tracking-widest">
                MCJUNKINS GROUP // DIGITAL TWIN PROTOTYPE v0.2
            </div>
        </div>
    );
};

export default NetworkStack;
